import {proposeLinkedScreens} from './linkedScreens';
import { syncComponentDeclarations } from '../components/componentProps';
import { renameOperand } from './computedModel';
import { otherPagePatch } from './pageKinds';
import { updateDashboard, dashboardIssues, type DashboardAction } from './dashboardModel';
/**
 * UPT-2079 L3 (§5.3) — ÉTAT du Concepteur : réducteur PUR (testé par `designerState.test.ts`) sur un Module Spec v1.1
 * en mémoire (`spec`), la disposition du canevas (`layout`, jamais dans le document), la sélection (tiroir ouvert),
 * l'état d'autosauvegarde et les contrôles instantanés (`moduleSpecLocalCheck`).
 *
 * Règles (§5.3.1) : chaque action de contenu produit un NOUVEL état (`dirty = true`, `localIssues` recalculés) ; toute
 * action qui viole une borne (`DESIGNER_LIMITS`) ou un motif est SANS EFFET (même référence d'état rendue) ; les
 * cascades (`entity.rename`, `entity.remove`, `field.rename`, `field.remove`) réécrivent toutes les références listées
 * dans la spec ; le réducteur n'écrit que des valeurs admises par le schéma et n'invente aucune clé hors `$defs`.
 */
import {
  DESIGNER_ARCHIMATE_TYPES,
  DESIGNER_LIMITS,
  type DesignModuleSpecJson,
  type DesignerNodePosition,
  type DesignerSelection,
  type ModuleSpecDraft,
  type ModuleSpecDraftLayout,
  type ModuleSpecDraftValidation,
} from '../data/contracts';
import type { ValidationIssue } from '../data/moduleSpecContracts';
import { moduleSpecLocalCheck } from '../modulespec/moduleSpecLocalCheck';
import type { DesignUiError } from '../specs/designErrors';
import { autoLayout } from './autoLayout';
import {
  CARDINALITIES,
  ENTITY_KINDS,
  FIELD_MODES,
  FIELD_TYPES,
  LOCALES,
  MISSING_MODULE,
  MODULE_CATEGORIES,
  PAGE_KINDS,
  PORTALS,
  RULE_KINDS,
  SORT_DIRECTIONS,
  VALIDATOR_TYPES,
  CLASSIFICATION_LEVELS,
  DEFAULT_TOKENIZE,
} from './catalogDefaults';
import {
  cascadeEntityInPages,
  cascadePageKeyInActions,
  cleanI18n,
  reconcileActionSlots,
  sanitizeActions,
  sanitizeEntityList,
  sanitizeLayout,
  sanitizePrototypeRef,
  sanitizeSlots,
  usesCustomPageProperties,
} from './customPageModel';
import { canonicalJson } from './specChecksum';
import {
  bumpNavigationVersion,
  dropTarget,
  groupAdd,
  groupMove,
  groupRemove,
  groupUpdate,
  itemAdd,
  itemMove,
  itemRemove,
  itemUpdate,
  navigationReplace,
  normalizeNavigation,
  type NavigationGroupPatch,
  type NavigationItemPatch,
  type NavigationTarget,
} from './navigationModel';
import {
  DESCRIPTION_MAX,
  DESCRIPTION_MIN,
  DOC_MIN,
  DOMAIN_PATTERN,
  MODULE_CODE_PATTERN,
  PERMISSION_ENTITY_PATTERN,
  SCHEMA_PATTERN,
  SEMVER_PATTERN,
  SLUG_PATTERN,
  SUMMARY_MAX,
  SUMMARY_MIN,
  domainForCode,
  firstFree,
  newEntity,
  newField,
  newPage,
  schemaForCode,
} from './specDefaults';
import {
  asArray,
  asRecord,
  asRecords,
  asString,
  asStrings,
  cloneJson,
  entityPluralLabel,
  entityLabel,
  readEntity,
  readModule,
  type EntityPatch,
  type Json,
  type ModuleSpecField,
  type ModuleSpecModule,
  type ModuleSpecPage,
  type ModuleSpecRelation,
  type ModuleSpecRule,
  type ModuleSpecScreens,
} from './specTypes';

import { canUseCreateKey } from './ownershipModel';
import {collectScreenReviews,reviewContent,movePageReviews,type ReviewedBlock} from './screenReview';
export type SaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'conflict' | 'error';

export interface DesignerState {
  spec: DesignModuleSpecJson;
  layout: ModuleSpecDraftLayout;
  selection: DesignerSelection;
  /** Différent du dernier brouillon enregistré. */
  dirty: boolean;
  /** Checksum du dernier `ModuleSpecDraft` reçu (`baseChecksum`). */
  checksum: string | null;
  /** Rapport serveur (ou local en démonstration). */
  validation: ModuleSpecDraftValidation | null;
  /** Contrôles instantanés (`moduleSpecLocalCheck`), recalculés à chaque action. */
  localIssues: ValidationIssue[];
  save: { status: SaveStatus; at: string | null; error: DesignUiError | null };
}

export type DesignerAction = {type:'screens.linked.generate';pivot:string} | DashboardAction
  | {type:'replay.replace';spec:DesignModuleSpecJson}
  | {type:'relations.apply';spec:DesignModuleSpecJson;origin:DesignModuleSpecJson}
  | { type: 'dashboard.review'; blocks: ReviewedBlock[] }
  | {type:'field.computed.set';entity:string;index:number;computed:NonNullable<ModuleSpecField['computed']>}
  | {type:'field.computed.clear';entity:string;index:number}
  | { type: 'load'; draft: ModuleSpecDraft }
  | { type: 'select'; selection: DesignerSelection }
  | { type: 'module.update'; patch: Partial<ModuleSpecModule> }
  | { type: 'presentation.set'; presentation: Record<string, unknown> | null }
  | { type: 'entity.add'; code?: string; position?: DesignerNodePosition }
  | { type: 'entity.update'; code: string; patch: EntityPatch }
  | { type: 'entity.rename'; code: string; next: string }
  | { type: 'entity.remove'; code: string }
  | { type: 'entity.move'; code: string; position: DesignerNodePosition }
  | { type: 'field.add'; entity: string; field?: Partial<ModuleSpecField> }
  | { type: 'field.update'; entity: string; index: number; patch: Partial<ModuleSpecField> }
  | { type: 'field.rename'; entity: string; index: number; next: string }
  | { type: 'field.move'; entity: string; from: number; to: number }
  | { type: 'field.remove'; entity: string; index: number }
  | { type: 'screens.update'; entity: string; patch: Partial<ModuleSpecScreens> }
  | { type: 'rule.add' | 'rule.update' | 'rule.remove'; entity: string; index?: number; rule?: ModuleSpecRule }
  | { type: 'relation.add'; source: string; target: string; targetModule?: string }
  | { type: 'relation.update'; code: string; patch: Partial<ModuleSpecRelation> }
  | { type: 'relation.remove'; code: string }
  | { type: 'page.screen.review'; key: string; blocks: ReviewedBlock[] }
  | { type: 'page.add' | 'page.update' | 'page.remove'; key?: string; patch?: Partial<ModuleSpecPage> }
  | { type: 'navigation.move'; from: number; to: number }
  | { type: 'navigation.toggle'; item: { entity?: string; page?: string } }
  /** Page d'accueil (`navigation.homePage`) : code d'entité ou clé de page du document (ajout hors liste §5.3 : bouton radio de §5.6.4). */
  | { type: 'navigation.home'; key: string }
  /** F2 — éditeur de menu v1.3 : groupes (`navigation.menus[]`) et éléments (objet, page, tableau de bord, séparateur). */
  | { type: 'navigation.group.add'; label: string }
  | { type: 'navigation.group.update'; index: number; patch: NavigationGroupPatch }
  | { type: 'navigation.group.remove'; index: number }
  | { type: 'navigation.group.move'; from: number; to: number }
  | { type: 'navigation.item.add'; group: number; target: NavigationTarget; at?: number }
  | { type: 'navigation.item.update'; group: number; index: number; patch: NavigationItemPatch }
  | { type: 'navigation.item.remove'; group: number; index: number }
  | { type: 'navigation.item.move'; group: number; from: number; to: number; toGroup?: number }
  /** Applique la navigation proposée par le serveur (menu du prototype) ; remplace les groupes existants. */
  | { type: 'navigation.replace'; navigation: unknown; addedPages?: unknown }
  | { type: 'save.pending' }
  | { type: 'save.started' }
  | { type: 'save.done'; draft: ModuleSpecDraft; savedSpec?:DesignModuleSpecJson }
  | { type: 'save.conflict'; serverChecksum: string }
  | { type: 'save.failed'; error: DesignUiError }
  | { type: 'reload'; draft: ModuleSpecDraft };

const ENTITY_CODE = DESIGNER_LIMITS.entityCodePattern;
const FIELD_NAME = DESIGNER_LIMITS.fieldNamePattern;
const RELATION_CODE = DESIGNER_LIMITS.relationCodePattern;
const KEY = DESIGNER_LIMITS.keyPattern;
const TOKENIZE_PATTERN = /^(tokenize|encrypt|mask)-[a-z0-9-]+$/;
const RENDERER_PATTERN = /^[a-z0-9-]+\.[a-z0-9]+\.[a-z0-9-]+$/;
const TABLE_PATTERN = /^module_[a-z0-9_]+\.[a-z][a-z0-9_]*$/;

export const EMPTY_LAYOUT: ModuleSpecDraftLayout = { entities: {} };

export const initialDesignerState = (): DesignerState => ({
  spec: { specVersion: '1.1', module: {}, entities: {} },
  layout: EMPTY_LAYOUT,
  selection: null,
  dirty: false,
  checksum: null,
  validation: null,
  localIssues: [],
  save: { status: 'idle', at: null, error: null },
});

/* ── Aides ─────────────────────────────────────────────────────────────────── */

const entitiesOf = (spec: DesignModuleSpecJson): Json => asRecord(spec.entities) ?? {};
const entityCodes = (spec: DesignModuleSpecJson): string[] => Object.keys(entitiesOf(spec));
const relationsOf = (spec: DesignModuleSpecJson): Json[] => asRecords(spec.relations);
const pagesOf = (spec: DesignModuleSpecJson): Json[] => asRecords(spec.pages);
const menuOf = (spec: DesignModuleSpecJson): Json | null => asRecords(asRecord(spec.navigation)?.menus)[0] ?? null;

const setOrDelete = (record: Json, key: string, value: unknown): void => {
  if (value === undefined || value === null) delete record[key];
  else record[key] = value;
};

const roundPosition = (position: DesignerNodePosition): DesignerNodePosition | null => {
  const x = Math.round(Number(position?.x));
  const y = Math.round(Number(position?.y));
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  const max = DESIGNER_LIMITS.layoutCoordinateMax;
  if (Math.abs(x) > max || Math.abs(y) > max) return null;
  return { x, y };
};

/** Finalise une action de contenu : nouvel état, `dirty`, contrôles instantanés. */
const content = (
  state: DesignerState,
  spec: DesignModuleSpecJson,
  layout: ModuleSpecDraftLayout = state.layout,
  selection: DesignerSelection = state.selection
): DesignerState => ({
  ...state,
  spec: syncComponentDeclarations(spec),
  layout,
  selection,
  dirty: true,
  localIssues: moduleSpecLocalCheck(spec).errors,
  // Conflit : aucune sauvegarde tant que l'utilisateur n'a pas rechargé ; échec : nouvelle tentative à la prochaine modification.
  save: state.save.status === 'conflict' ? state.save : { status: 'pending', at: state.save.at, error: null },
});

/** Retire `name` d'une liste de chaînes d'un objet ; supprime la clé si la liste devient vide. */
const dropFromList = (owner: Json | null, key: string, name: string): void => {
  if (!owner || !Array.isArray(owner[key])) return;
  const next = asStrings(owner[key]).filter((entry) => entry !== name);
  if (next.length === 0) delete owner[key];
  else owner[key] = next;
};

const renameInList = (owner: Json | null, key: string, previous: string, next: string): void => {
  if (!owner || !Array.isArray(owner[key])) return;
  owner[key] = asArray(owner[key]).map((entry) => (entry === previous ? next : entry));
};

/* ── Cascades (§5.3.1) ─────────────────────────────────────────────────────── */

const cascadeEntityRename = (spec: DesignModuleSpecJson, layout: ModuleSpecDraftLayout, previous: string, next: string): ModuleSpecDraftLayout => {
  const entities = entitiesOf(spec);
  const schema = asString(readModule(spec).schema);
  const entity = asRecord(entities[previous]);
  if (entity && entity.table === `${schema}.${previous}`) entity.table = `${schema}.${next}`;
  const reordered: Json = {};
  Object.keys(entities).forEach((code) => {
    reordered[code === previous ? next : code] = entities[code];
  });
  spec.entities = reordered;
  relationsOf(spec).forEach((relation) => {
    const source = asRecord(relation.source);
    const target = asRecord(relation.target);
    if (source && source.entity === previous) source.entity = next;
    if (target && target.entity === previous && typeof target.module !== 'string') target.entity = next;
  });
  pagesOf(spec).forEach((page) => renameInList(page, 'entities', previous, next));
  cascadeEntityInPages(pagesOf(spec), previous, next);
  asRecords(asRecord(spec.navigation)?.menus).forEach((menu) =>
    asRecords(menu.items).forEach((item) => {
      if (item.entity === previous) item.entity = next;
    })
  );
  const navigation = asRecord(spec.navigation);
  if (navigation && navigation.homePage === previous) navigation.homePage = next;
  asRecords(spec.coreReuse).forEach((entry) => renameInList(entry, 'entities', previous, next));
  Object.values(entities).forEach((value) => {
    const other = asRecord(value);
    if (!other) return;
    const parent = asRecord(other.parent);
    if (parent && parent.entity === previous) parent.entity = next;
    asRecords(other.fields).forEach((field) => {
      const data = asRecord(field.data);
      if (data && data.type === 'store' && data.entity === previous) data.entity = next;
    });
  });
  const positions: Record<string, DesignerNodePosition> = {};
  Object.keys(layout.entities).forEach((code) => {
    positions[code === previous ? next : code] = layout.entities[code];
  });
  return { ...layout, entities: positions };
};

/** Retire l'entité et toutes ses références ; rend le nombre de relations retirées. */
export const cascadeEntityRemove = (spec: DesignModuleSpecJson, layout: ModuleSpecDraftLayout, code: string): { layout: ModuleSpecDraftLayout; relationsRemoved: number } => {
  const entities = entitiesOf(spec);
  delete entities[code];
  const before = relationsOf(spec).length;
  if (Array.isArray(spec.relations)) {
    spec.relations = relationsOf(spec).filter((relation) => {
      const source = asRecord(relation.source);
      const target = asRecord(relation.target);
      const sourceHit = source?.entity === code;
      const targetHit = target?.entity === code && typeof target?.module !== 'string';
      return !sourceHit && !targetHit;
    });
    if ((spec.relations as unknown[]).length === 0) delete spec.relations;
  }
  pagesOf(spec).forEach((page) => dropFromList(page, 'entities', code));
  cascadeEntityInPages(pagesOf(spec), code, null);
  const navigation = asRecord(spec.navigation);
  if (navigation) {
    asRecords(navigation.menus).forEach((menu) => {
      menu.items = asRecords(menu.items).filter((item) => item.entity !== code);
    });
    if (navigation.homePage === code) {
      const remaining = Object.keys(entities);
      if (remaining.length > 0) navigation.homePage = remaining[0];
      else delete navigation.homePage;
    }
    // F2 : un groupe vidé disparaît (le schéma exige au moins un élément), un séparateur orphelin aussi.
    normalizeNavigation(spec);
  }
  if (Array.isArray(spec.coreReuse)) {
    asRecords(spec.coreReuse).forEach((entry) => dropFromList(entry, 'entities', code));
    spec.coreReuse = asRecords(spec.coreReuse).filter((entry) => asStrings(entry.entities).length > 0);
    if ((spec.coreReuse as unknown[]).length === 0) delete spec.coreReuse;
  }
  Object.values(entities).forEach((value) => {
    const other = asRecord(value);
    if (other && asRecord(other.parent)?.entity === code) delete other.parent;
  });
  const positions = { ...layout.entities };
  delete positions[code];
  return { layout: { ...layout, entities: positions }, relationsRemoved: before - relationsOf(spec).length };
};

/** Nombre de références qu'un renommage de champ réécrirait (affiché par le tiroir avant « Appliquer »). */
export const countFieldReferences = (spec: DesignModuleSpecJson, entityCode: string, name: string): number => {
  const entity = asRecord(entitiesOf(spec)[entityCode]);
  if (!entity) return 0;
  let count = 0;
  const screens = asRecord(entity.screens);
  const list = asRecord(screens?.list);
  count += asStrings(list?.columns).filter((entry) => entry === name).length;
  if (asRecord(list?.defaultSort)?.field === name) count += 1;
  const card = asRecord(list?.mobileCard);
  (['title', 'subtitle', 'badge'] as const).forEach((slot) => {
    if (card?.[slot] === name) count += 1;
  });
  asRecords(asRecord(screens?.form)?.tabs).forEach((tab) => {
    count += asStrings(tab.fields).filter((entry) => entry === name).length;
  });
  if (entity.businessKey === name) count += 1;
  count += asStrings(entity.createKey).filter((entry) => entry === name).length;
  count += asStrings(entity.upsert).filter((entry) => entry === name).length;
  asRecords(entity.rules).forEach((rule) => {
    count += asStrings(rule.fields).filter((entry) => entry === name).length;
  });
  if (asRecord(entity.workflow)?.field === name) count += 1;
  relationsOf(spec).forEach((relation) => {
    const source = asRecord(relation.source);
    const target = asRecord(relation.target);
    if (source?.entity === entityCode && source?.field === name) count += 1;
    if (target?.entity === entityCode && typeof target?.module !== 'string' && target?.displayField === name) count += 1;
  });
  Object.values(entitiesOf(spec)).forEach((value) => {
    const parent = asRecord(asRecord(value)?.parent);
    if (parent?.entity === entityCode && parent?.field === name) count += 1;
  });
  return count;
};

/** Nombre de références qu'un renommage d'entité réécrirait. */
export const countEntityReferences = (spec: DesignModuleSpecJson, code: string): number => {
  let count = 0;
  relationsOf(spec).forEach((relation) => {
    if (asRecord(relation.source)?.entity === code) count += 1;
    const target = asRecord(relation.target);
    if (target?.entity === code && typeof target?.module !== 'string') count += 1;
  });
  pagesOf(spec).forEach((page) => {
    count += asStrings(page.entities).filter((entry) => entry === code).length;
  });
  asRecords(asRecord(spec.navigation)?.menus).forEach((menu) => {
    count += asRecords(menu.items).filter((item) => item.entity === code).length;
  });
  if (asRecord(spec.navigation)?.homePage === code) count += 1;
  asRecords(spec.coreReuse).forEach((entry) => {
    count += asStrings(entry.entities).filter((entry2) => entry2 === code).length;
  });
  Object.values(entitiesOf(spec)).forEach((value) => {
    const other = asRecord(value);
    if (asRecord(other?.parent)?.entity === code) count += 1;
    asRecords(other?.fields).forEach((field) => {
      const data = asRecord(field.data);
      if (data?.type === 'store' && data.entity === code) count += 1;
    });
  });
  return count;
};

const cascadeFieldRename = (spec: DesignModuleSpecJson, entityCode: string, previous: string, next: string): void => {
  const entity = asRecord(entitiesOf(spec)[entityCode]);
  if (!entity) return;
  const screens = asRecord(entity.screens);
  const list = asRecord(screens?.list);
  renameInList(list, 'columns', previous, next);
  const sort = asRecord(list?.defaultSort);
  if (sort && sort.field === previous) sort.field = next;
  const card = asRecord(list?.mobileCard);
  (['title', 'subtitle', 'badge'] as const).forEach((slot) => {
    if (card && card[slot] === previous) card[slot] = next;
  });
  asRecords(asRecord(screens?.form)?.tabs).forEach((tab) => renameInList(tab, 'fields', previous, next));
  if (entity.businessKey === previous) entity.businessKey = next;
  renameInList(entity, 'createKey', previous, next);
  renameInList(entity, 'upsert', previous, next);
  asRecords(entity.rules).forEach((rule) => renameInList(rule, 'fields', previous, next));
  const workflow = asRecord(entity.workflow);
  if (workflow && workflow.field === previous) workflow.field = next;
  relationsOf(spec).forEach((relation) => {
    const source = asRecord(relation.source);
    const target = asRecord(relation.target);
    if (source && source.entity === entityCode && source.field === previous) source.field = next;
    if (target && target.entity === entityCode && typeof target.module !== 'string' && target.displayField === previous) target.displayField = next;
  });
  Object.values(entitiesOf(spec)).forEach((value) => {
    const parent = asRecord(asRecord(value)?.parent);
    if (parent && parent.entity === entityCode && parent.field === previous) parent.field = next;
  });
};

const cascadeFieldRemove = (spec: DesignModuleSpecJson, entityCode: string, name: string): void => {
  const entity = asRecord(entitiesOf(spec)[entityCode]);
  if (!entity) return;
  const screens = asRecord(entity.screens);
  const list = asRecord(screens?.list);
  dropFromList(list, 'columns', name);
  if (asRecord(list?.defaultSort)?.field === name && list) delete list.defaultSort;
  const card = asRecord(list?.mobileCard);
  (['title', 'subtitle', 'badge'] as const).forEach((slot) => {
    if (card && card[slot] === name) delete card[slot];
  });
  if (card && Object.keys(card).length === 0 && list) delete list.mobileCard;
  const form = asRecord(screens?.form);
  if (form && Array.isArray(form.tabs)) {
    asRecords(form.tabs).forEach((tab) => dropFromList(tab, 'fields', name));
    form.tabs = asRecords(form.tabs).filter((tab) => asStrings(tab.fields).length > 0);
    if ((form.tabs as unknown[]).length === 0) delete form.tabs;
  }
  // Objets vidés par la cascade : retirés (le schéma n'admet pas `columns` ni `tabs` vides).
  if (form && Object.keys(form).length === 0 && screens) delete screens.form;
  if (list && Object.keys(list).length === 0 && screens) delete screens.list;
  if (screens && Object.keys(screens).length === 0) delete entity.screens;
  if (entity.businessKey === name) delete entity.businessKey;
  // Removing any key member requires reviewing a new key explicitly.
  if (asStrings(entity.createKey).includes(name)) delete entity.createKey;
  dropFromList(entity, 'upsert', name);
  if (Array.isArray(entity.rules)) {
    asRecords(entity.rules).forEach((rule) => dropFromList(rule, 'fields', name));
    entity.rules = asRecords(entity.rules).filter((rule) => asStrings(rule.fields).length > 0);
    if ((entity.rules as unknown[]).length === 0) delete entity.rules;
  }
  const workflow = asRecord(entity.workflow);
  if (workflow && workflow.field === name) delete workflow.field;
  relationsOf(spec).forEach((relation) => {
    const source = asRecord(relation.source);
    const target = asRecord(relation.target);
    if (source && source.entity === entityCode && source.field === name) delete source.field;
    if (target && target.entity === entityCode && typeof target.module !== 'string' && target.displayField === name) delete target.displayField;
  });
  Object.values(entitiesOf(spec)).forEach((value) => {
    const other = asRecord(value);
    const parent = asRecord(other?.parent);
    if (other && parent && parent.entity === entityCode && parent.field === name) delete other.parent;
  });
};

/* ── Normalisation d'un champ (§5.3.1 `field.update`) ──────────────────────── */

const DATA_STATIC_TYPES = new Set(['select', 'radio', 'checkbox']);
const DATA_REF_TYPES = new Set(['typeahead', 'entityref']);

/** Applique un patch à un champ ; rend null quand le patch est refusé (type inconnu, 51ᵉ option, motif). */
const patchField = (field: Json, patch: Partial<ModuleSpecField>): Json | null => {
  const next: Json = { ...field };
  const raw = patch as Json;
  if ('form' in raw) setOrDelete(next, 'form', raw.form ? cloneJson(raw.form) : undefined);
  if ('type' in raw) {
    if (!FIELD_TYPES.includes(asString(raw.type))) return null;
    next.type = raw.type;
  }
  (['label', 'help'] as const).forEach((slot) => {
    if (slot in raw) setOrDelete(next, slot, cleanI18n(raw[slot]));
  });
  if ('doc' in raw) setOrDelete(next, 'doc', typeof raw.doc === 'string' && raw.doc.length > 0 ? raw.doc.slice(0, 2000) : undefined);
  (['required', 'unique'] as const).forEach((slot) => {
    if (slot in raw) setOrDelete(next, slot, raw[slot] === true ? true : undefined);
  });
  if ('mode' in raw) setOrDelete(next, 'mode', FIELD_MODES.includes(asString(raw.mode)) ? raw.mode : undefined);
  if ('default' in raw) {
    const type = asString(next.type);
    const value = raw.default;
    if (value === undefined || value === null || value === '') delete next.default;
    else if (type === 'number' || type === 'amount') {
      const number = typeof value === 'number' ? value : Number(value);
      if (Number.isFinite(number)) next.default = number;
      else delete next.default;
    } else if (type === 'checkbox') next.default = value === true || value === 'true';
    else next.default = typeof value === 'string' ? value : typeof value === 'number' || typeof value === 'boolean' ? String(value) : JSON.stringify(value);
  }
  if ('protection' in raw) {
    const incoming = asRecord(raw.protection);
    if (!incoming) delete next.protection;
    else {
      const protection: Json = { ...(asRecord(next.protection) ?? {}) };
      if ('classification' in incoming) setOrDelete(protection, 'classification', CLASSIFICATION_LEVELS.includes(asString(incoming.classification)) ? incoming.classification : undefined);
      if ('searchable' in incoming) setOrDelete(protection, 'searchable', typeof incoming.searchable === 'boolean' ? incoming.searchable : undefined);
      if ('tokenize' in incoming) setOrDelete(protection, 'tokenize', TOKENIZE_PATTERN.test(asString(incoming.tokenize)) ? incoming.tokenize : undefined);
      if ('personalData' in incoming) {
        if (incoming.personalData === true) {
          protection.personalData = true;
          if (['text','textarea','richtext','email','phone','color','link','select','radio','typeahead','multiselect'].includes(asString(next.type))) {
            if (!TOKENIZE_PATTERN.test(asString(protection.tokenize))) protection.tokenize = DEFAULT_TOKENIZE;
          } else { delete protection.tokenize; protection.classification = 'C3'; }
        } else {
          delete protection.personalData;
          delete protection.tokenize;
        }
      }
      if (Object.keys(protection).length === 0) delete next.protection;
      else next.protection = protection;
    }
  }
  if ('validators' in raw) {
    const validators = asRecords(raw.validators)
      .filter((validator) => VALIDATOR_TYPES.includes(asString(validator.type)))
      .map((validator) => {
        const out: Json = { type: validator.type };
        if (validator.value !== undefined && validator.value !== null && validator.value !== '') out.value = validator.value;
        const message = cleanI18n(validator.message);
        if (message) out.message = message;
        return out;
      });
    if (validators.length === 0) delete next.validators;
    else next.validators = validators;
  }
  if ('grid' in raw) {
    const incoming = asRecord(raw.grid);
    if (!incoming) delete next.grid;
    else {
      const grid: Json = {};
      if (typeof incoming.visible === 'boolean') grid.visible = incoming.visible;
      const width = Number(incoming.width);
      if (Number.isFinite(width) && incoming.width !== '' && incoming.width !== null && incoming.width !== undefined) {
        grid.width = Math.min(800, Math.max(40, Math.round(width)));
      }
      if (Object.keys(grid).length === 0) delete next.grid;
      else next.grid = grid;
    }
  }
  if ('data' in raw) {
    const incoming = asRecord(raw.data);
    if (!incoming) delete next.data;
    else next.data = incoming;
  }
  // Cohérence `data` / `type` : `static` pour select / radio / checkbox ; `store` / `entityref` pour typeahead / entityref ; retiré sinon.
  const type = asString(next.type);
  const data = asRecord(next.data);
  if (data) {
    const kind = asString(data.type);
    const allowed = DATA_STATIC_TYPES.has(type) ? kind === 'static' : DATA_REF_TYPES.has(type) ? kind === 'store' || kind === 'entityref' : false;
    if (!allowed) delete next.data;
    else if (kind === 'static') {
      const options = asRecords(data.options).map((option) => ({ value: asString(option.value).slice(0, 64), label: cleanI18n(option.label) ?? { fr: asString(option.value) } }));
      if (options.length > DESIGNER_LIMITS.staticOptions) return null;
      next.data = { type: 'static', options };
    } else if (kind === 'store') {
      const out: Json = { type: 'store', entity: asString(data.entity), valueField: asString(data.valueField), displayField: asString(data.displayField) };
      if (typeof data.compactDisplayField === 'string' && data.compactDisplayField) out.compactDisplayField = data.compactDisplayField;
      if (asRecord(data.filter)) out.filter = data.filter;
      if (asRecord(data.cascade)) out.cascade = data.cascade;
      next.data = out;
    } else {
      const out: Json = { type: 'entityref', module: asString(data.module), entity: asString(data.entity), valueField: asString(data.valueField), displayField: asString(data.displayField) };
      if (data.multiple === true) out.multiple = true;
      if (MISSING_MODULE.includes(asString(data.missingModule))) out.missingModule = data.missingModule;
      next.data = out;
    }
  }
  return next;
};

/* ── Réducteur ─────────────────────────────────────────────────────────────── */

const loadFrom = (state: DesignerState, draft: ModuleSpecDraft, keepSelection: boolean): DesignerState => {
  const spec = cloneJson(draft.spec);
  const received: ModuleSpecDraftLayout = { ...(draft.layout?.screenReviews ? {screenReviews:cloneJson(draft.layout.screenReviews)} : {}), entities: { ...(asRecord(draft.layout)?.entities as Record<string, DesignerNodePosition> | undefined ?? {}) } };
  const layout = autoLayout(spec, received);
  const completed = canonicalJson(layout) !== canonicalJson(received);
  const codes = entityCodes(spec);
  const selection = keepSelection && state.selection && (state.selection.kind !== 'entity' || codes.includes(state.selection.code)) ? state.selection : null;
  return {
    spec,
    layout,
    selection,
    dirty: completed,
    checksum: draft.checksum,
    validation: draft.validation ?? null,
    localIssues: moduleSpecLocalCheck(spec).errors,
    save: { status: completed ? 'pending' : 'saved', at: draft.updatedAt ?? null, error: null },
  };
};

export function designerReducer(state: DesignerState, action: DesignerAction): DesignerState {
  if(action.type==='dashboard.review') {
    const spec={...state.spec,dashboard:{...asRecord(state.spec.dashboard),widgets:action.blocks.map(reviewContent)}};
    const reviews={...state.layout.screenReviews};
    Object.keys(reviews).filter(key=>key.startsWith('dashboard/')).forEach(key=>delete reviews[key]);
    return content(state,spec,{...state.layout,screenReviews:collectScreenReviews(action.blocks,'dashboard',reviews)});
  }
  if (action.type.startsWith('dashboard.')) {
    const spec = updateDashboard(state.spec, action as DashboardAction);
    return spec ? { ...content(state, spec), localIssues: [...moduleSpecLocalCheck(spec).errors, ...dashboardIssues(spec)] } : state;
  }
  switch (action.type) {
    case 'replay.replace': return {...content(state,action.spec),spec:action.spec};
    case 'relations.apply':
      return state.spec===action.origin?content(state,cloneJson(action.spec),autoLayout(action.spec,state.layout)):state;
    case 'screens.linked.generate': {
      const proposal=proposeLinkedScreens(state.spec,action.pivot);
      if(!proposal.pages.length)return state;
      const spec=cloneJson(state.spec);
      spec.pages=pagesOf(spec).map(page=>proposal.pages.find(item=>item.key===page.key)??page);
      return content(state,spec);
    }
    case 'page.screen.review': {
      if (!pagesOf(state.spec).some(page => page.key === action.key && page.kind === 'custom')) return state;
      const next=designerReducer(state,{type:'page.update',key:action.key,patch:{screen:{columns:12,blocks:action.blocks.map(reviewContent)}}});
      if(next===state)return state;
      const reviews={...state.layout.screenReviews};
      Object.keys(reviews).filter(key=>key.startsWith(action.key+'/')).forEach(key=>delete reviews[key]);
      return content(next,next.spec,{...next.layout,screenReviews:collectScreenReviews(action.blocks,action.key,reviews)});
    }
    case 'load':
      return loadFrom(state, action.draft, false);
    case 'reload':
      return loadFrom(state, action.draft, true);
    case 'select':
      return { ...state, selection: action.selection };

    case 'module.update': {
      const spec = cloneJson(state.spec);
      const module = asRecord(spec.module) ?? {};
      const patch = action.patch as Json;
      const previousCode = asString(module.code);
      if ('code' in patch) {
        if (!MODULE_CODE_PATTERN.test(asString(patch.code))) return state;
        module.code = patch.code;
        if (!asString(module.schema) || module.schema === schemaForCode(previousCode)) module.schema = schemaForCode(asString(patch.code));
      }
      if ('schema' in patch && !('code' in patch)) {
        if (!SCHEMA_PATTERN.test(asString(patch.schema))) return state;
        module.schema = patch.schema;
      }
      if ('domain' in patch) {
        if (!DOMAIN_PATTERN.test(asString(patch.domain))) return state;
        module.domain = patch.domain;
      }
      if ('name' in patch) {
        const name = asString(patch.name).trim();
        if (name.length < 2 || name.length > 80) return state;
        module.name = name;
      }
      if ('category' in patch) {
        if (!MODULE_CATEGORIES.includes(asString(patch.category))) return state;
        module.category = patch.category;
      }
      if ('version' in patch) {
        if (!SEMVER_PATTERN.test(asString(patch.version))) return state;
        module.version = patch.version;
      }
      if ('summary' in patch) {
        const summary = asString(patch.summary).trim();
        if (summary.length < SUMMARY_MIN || summary.length > SUMMARY_MAX) return state;
        module.summary = summary;
      }
      if ('vendor' in patch) setOrDelete(module, 'vendor', asString(patch.vendor).trim().slice(0, 80) || undefined);
      if ('description' in patch) setOrDelete(module, 'description', asString(patch.description).slice(0, 4000) || undefined);
      if ('locales' in patch) {
        const locales = asStrings(patch.locales).filter((locale) => LOCALES.includes(locale));
        if (locales.length === 0 || !locales.includes('fr')) return state;
        module.locales = [...new Set(locales)];
      }
      if ('portals' in patch) {
        const portals = [...new Set(asStrings(patch.portals).filter((portal) => PORTALS.includes(portal)))];
        if (portals.length === 0) return state;
        module.portals = portals;
      }
      spec.module = module;
      return content(state, spec);
    }

    case 'presentation.set': {
      // UPT-2079 L14a : le bloc `presentation` (schéma v1.2) est remplacé en entier ou retiré ; `specVersion` passe à « 1.2 » (le serveur le ferait de toute façon).
      const spec = cloneJson(state.spec);
      if (action.presentation === null) {
        if (!('presentation' in spec)) return state;
        delete spec.presentation;
      } else {
        if (typeof action.presentation !== 'object' || Array.isArray(action.presentation)) return state;
        if (canonicalJson(spec.presentation ?? null) === canonicalJson(action.presentation)) return state;
        spec.presentation = cloneJson(action.presentation) as unknown as Json;
        if (spec.specVersion === '1.0' || spec.specVersion === '1.1') spec.specVersion = '1.2';
      }
      return content(state, spec);
    }

    case 'entity.add': {
      const codes = entityCodes(state.spec);
      if (codes.length >= DESIGNER_LIMITS.entities) return state;
      const free = firstFree('entite', codes);
      const code = action.code ?? free.code;
      if (!ENTITY_CODE.test(code) || codes.includes(code)) return state;
      const spec = cloneJson(state.spec);
      const schema = asString(readModule(spec).schema) || schemaForCode(asString(readModule(spec).code) || 'module');
      const entities = asRecord(spec.entities) ?? {};
      entities[code] = newEntity(action.code ? codes.length + 1 : free.index, schema, code) as unknown as Json;
      spec.entities = entities;
      const position = action.position ? roundPosition(action.position) : null;
      const layout = position
        ? { ...state.layout, entities: { ...state.layout.entities, [code]: position } }
        : autoLayout(spec, state.layout);
      return content(state, spec, layout, { kind: 'entity', code });
    }

    case 'entity.update': {
      const spec = cloneJson(state.spec);
      const entity = asRecord(entitiesOf(spec)[action.code]);
      if (!entity) return state;
      const patch = action.patch as Json;
      if ('label' in patch) {
        const label = asRecord(patch.label);
        const singular = cleanI18n(label?.singular);
        const plural = cleanI18n(label?.plural);
        if (!singular || !plural) return state;
        entity.label = { singular, plural };
      }
      if ('doc' in patch) {
        const doc = asString(patch.doc);
        if (doc.trim().length < DOC_MIN) return state;
        entity.doc = doc.slice(0, 4000);
      }
      if ('kind' in patch) {
        if (!ENTITY_KINDS.includes(asString(patch.kind))) return state;
        entity.kind = patch.kind;
      }
      if ('table' in patch) {
        const table = asString(patch.table);
        if (table && !TABLE_PATTERN.test(table)) return state;
        setOrDelete(entity, 'table', table || undefined);
      }
      if ('slugs' in patch) {
        const slugs = asRecord(patch.slugs);
        const out: Json = {};
        for (const slot of ['singular', 'plural'] as const) {
          const value = asString(slugs?.[slot]);
          if (value) {
            if (!KEY.test(value)) return state;
            out[slot] = value;
          }
        }
        setOrDelete(entity, 'slugs', Object.keys(out).length > 0 ? out : undefined);
      }
      if ('scope' in patch) {
        const scope = asRecord(patch.scope);
        const out: Json = {};
        if (typeof scope?.workspace === 'boolean') out.workspace = scope.workspace;
        if (typeof scope?.year === 'boolean') out.year = scope.year;
        setOrDelete(entity, 'scope', Object.keys(out).length > 0 ? out : undefined);
      }
      const names = asRecords(entity.fields).map((field) => asString(field.name));
      if ('ownership' in patch) {
        if (patch.ownership !== undefined && patch.ownership !== 'user') return state;
        setOrDelete(entity, 'ownership', patch.ownership);
        if (patch.ownership) spec.specVersion = '1.8';
      }
      if ('createKey' in patch) {
        const key = patch.createKey;
        if (key !== undefined && (!Array.isArray(key) || key.length < 1 || key.length > 8 || new Set(key).size !== key.length || key.some(name => !asRecords(entity.fields).some(field => field.name === name && canUseCreateKey(field as unknown as ModuleSpecField))))) return state;
        setOrDelete(entity, 'createKey', key);
        if (key) spec.specVersion = '1.8';
      }
      if ('businessKey' in patch) {
        const key = asString(patch.businessKey);
        if (key && !names.includes(key)) return state;
        setOrDelete(entity, 'businessKey', key || undefined);
      }
      if ('upsert' in patch) {
        const upsert = asStrings(patch.upsert).filter((name) => names.includes(name));
        setOrDelete(entity, 'upsert', upsert.length > 0 ? [...new Set(upsert)] : undefined);
      }
      if ('permissionEntity' in patch) {
        const value = asString(patch.permissionEntity);
        if (value && !PERMISSION_ENTITY_PATTERN.test(value)) return state;
        setOrDelete(entity, 'permissionEntity', value || undefined);
      }
      if ('parent' in patch) {
        const parent = asRecord(patch.parent);
        const parentEntity = asString(parent?.entity);
        const parentField = asString(parent?.field);
        if (!parentEntity) delete entity.parent;
        else {
          if (parentEntity === action.code || !entityCodes(spec).includes(parentEntity) || !parentField || !names.includes(parentField)) return state;
          entity.parent = { entity: parentEntity, field: parentField };
        }
      }
      if ('customRenderers' in patch) {
        const renderers = asRecord(patch.customRenderers);
        const out: Json = {};
        for (const slot of ['list', 'form'] as const) {
          const value = asString(renderers?.[slot]);
          if (value) {
            if (!RENDERER_PATTERN.test(value)) return state;
            out[slot] = value;
          }
        }
        setOrDelete(entity, 'customRenderers', Object.keys(out).length > 0 ? out : undefined);
      }
      return content(state, spec);
    }

    case 'entity.rename': {
      const codes = entityCodes(state.spec);
      if (!codes.includes(action.code) || action.next === action.code) return state;
      if (!ENTITY_CODE.test(action.next) || codes.includes(action.next)) return state;
      const spec = cloneJson(state.spec);
      const layout = cascadeEntityRename(spec, state.layout, action.code, action.next);
      const selection: DesignerSelection =
        state.selection?.kind === 'entity' && state.selection.code === action.code
          ? { kind: 'entity', code: action.next }
          : state.selection?.kind === 'field' && state.selection.entity === action.code
            ? { kind: 'field', entity: action.next, index: state.selection.index }
            : state.selection;
      return content(state, spec, layout, selection);
    }

    case 'entity.remove': {
      if (!entityCodes(state.spec).includes(action.code)) return state;
      const spec = cloneJson(state.spec);
      const { layout } = cascadeEntityRemove(spec, state.layout, action.code);
      const current = state.selection;
      const gone =
        (current?.kind === 'entity' && current.code === action.code) ||
        (current?.kind === 'field' && current.entity === action.code) ||
        (current?.kind === 'relation' && !relationsOf(spec).some((relation) => relation.code === current.code));
      const selection: DesignerSelection = gone ? null : current;
      return content(state, spec, layout, selection);
    }

    case 'entity.move': {
      if (!entityCodes(state.spec).includes(action.code)) return state;
      const position = roundPosition(action.position);
      if (!position) return state;
      const current = state.layout.entities[action.code];
      if (current && current.x === position.x && current.y === position.y) return state;
      return content(state, state.spec, { ...state.layout, entities: { ...state.layout.entities, [action.code]: position } });
    }

    case 'field.add': {
      const spec = cloneJson(state.spec);
      const entity = asRecord(entitiesOf(spec)[action.entity]);
      if (!entity) return state;
      const fields = asRecords(entity.fields);
      if (fields.length >= DESIGNER_LIMITS.fieldsPerEntity) return state;
      const names = fields.map((field) => asString(field.name));
      const free = firstFree('champ', names);
      const base = newField(free.index) as unknown as Json;
      const requested = asString(action.field?.name);
      if (requested) {
        if (!FIELD_NAME.test(requested) || names.includes(requested)) return state;
        base.name = requested;
        base.label = { fr: requested };
      }
      const name = asString(base.name);
      const patched = action.field ? patchField(base, { ...action.field, name: undefined } as Partial<ModuleSpecField>) : { ...base };
      if (!patched) return state;
      delete patched.name;
      const field: Json = { name, ...patched };
      entity.fields = [...fields, field];
      const tabs = asRecords(asRecord(asRecord(entity.screens)?.form)?.tabs);
      if (tabs.length > 0) {
        const last = tabs[tabs.length - 1];
        last.fields = [...asStrings(last.fields), asString(field.name)];
      }
      return content(state, spec, state.layout, { kind: 'field', entity: action.entity, index: fields.length });
    }

    case 'field.computed.set':
    case 'field.computed.clear': {
      const spec=cloneJson(state.spec),entity=asRecord(entitiesOf(spec)[action.entity]);
      if(!entity)return state;
      const field=asRecords(entity.fields)[action.index];if(!field)return state;
      if(action.type==='field.computed.clear')delete field.computed;
      else {
        if(!['number','amount'].includes(asString(field.type)))return state;
        field.computed={...action.computed};field.mode='READONLY';
        for(const key of ['required','unique','default','validators','data'])delete field[key];
        if (action.computed.serverOnly || /\bscore\s*\(/.test(action.computed.formula)) spec.specVersion='1.8';
        else if (Number(spec.specVersion) < 1.4) spec.specVersion='1.4';
      }
      return content(state,spec);
    }
    case 'field.update': {
      const spec = cloneJson(state.spec);
      const entity = asRecord(entitiesOf(spec)[action.entity]);
      if (!entity) return state;
      const fields = asRecords(entity.fields);
      const current = fields[action.index];
      if (!current) return state;
      const { name: _ignored, ...patch } = action.patch as Json;
      void _ignored;
      const next = patchField(current, patch as Partial<ModuleSpecField>);
      if (!next) return state;
      fields[action.index] = next;
      entity.fields = fields;
      return content(state, spec);
    }

    case 'field.rename': {
      const spec = cloneJson(state.spec);
      const entity = asRecord(entitiesOf(spec)[action.entity]);
      if (!entity) return state;
      const fields = asRecords(entity.fields);
      const current = fields[action.index];
      if (!current) return state;
      const previous = asString(current.name);
      if (previous === action.next) return state;
      if (!FIELD_NAME.test(action.next) || fields.some((field) => field.name === action.next)) return state;
      current.name = action.next;
      for(const field of fields) {
        const computed=asRecord(field.computed);
        if(computed && typeof computed.formula==='string') {
          try {computed.formula=renameOperand(computed.formula,previous,action.next);}
          catch { /* Une formule déjà invalide reste visible pour correction. */ }
        }
      }
      cascadeFieldRename(spec, action.entity, previous, action.next);
      return content(state, spec);
    }

    case 'field.move': {
      const spec = cloneJson(state.spec);
      const entity = asRecord(entitiesOf(spec)[action.entity]);
      if (!entity) return state;
      const fields = asRecords(entity.fields);
      if (action.from === action.to || !fields[action.from] || action.to < 0 || action.to >= fields.length) return state;
      const [moved] = fields.splice(action.from, 1);
      fields.splice(action.to, 0, moved);
      entity.fields = fields;
      const selection: DesignerSelection =
        state.selection?.kind === 'field' && state.selection.entity === action.entity && state.selection.index === action.from
          ? { kind: 'field', entity: action.entity, index: action.to }
          : state.selection;
      return content(state, spec, state.layout, selection);
    }

    case 'field.remove': {
      const spec = cloneJson(state.spec);
      const entity = asRecord(entitiesOf(spec)[action.entity]);
      if (!entity) return state;
      const fields = asRecords(entity.fields);
      const current = fields[action.index];
      if (!current) return state;
      fields.splice(action.index, 1);
      entity.fields = fields;
      cascadeFieldRemove(spec, action.entity, asString(current.name));
      const selection: DesignerSelection =
        state.selection?.kind === 'field' && state.selection.entity === action.entity
          ? { kind: 'entity', code: action.entity }
          : state.selection;
      return content(state, spec, state.layout, selection);
    }

    case 'screens.update': {
      const spec = cloneJson(state.spec);
      const entity = asRecord(entitiesOf(spec)[action.entity]);
      if (!entity) return state;
      const names = asRecords(entity.fields).map((field) => asString(field.name));
      const screens: Json = { ...(asRecord(entity.screens) ?? {}) };
      const patch = action.patch as Json;
      if ('list' in patch) {
        const incoming = asRecord(patch.list);
        if (!incoming) delete screens.list;
        else {
          const list: Json = { ...(asRecord(screens.list) ?? {}) };
          if ('columns' in incoming) {
            const columns = [...new Set(asStrings(incoming.columns).filter((name) => names.includes(name)))].slice(0, 15);
            setOrDelete(list, 'columns', columns.length > 0 ? columns : undefined);
          }
          if ('defaultSort' in incoming) {
            const sort = asRecord(incoming.defaultSort);
            const field = asString(sort?.field);
            if (!field || !names.includes(field)) delete list.defaultSort;
            else list.defaultSort = SORT_DIRECTIONS.includes(asString(sort?.direction)) ? { field, direction: sort?.direction } : { field };
          }
          if ('mobileCard' in incoming) {
            const card = asRecord(incoming.mobileCard);
            const out: Json = {};
            (['title', 'subtitle', 'badge'] as const).forEach((slot) => {
              const value = asString(card?.[slot]);
              if (value && names.includes(value)) out[slot] = value;
            });
            setOrDelete(list, 'mobileCard', Object.keys(out).length > 0 ? out : undefined);
          }
          setOrDelete(screens, 'list', Object.keys(list).length > 0 ? list : undefined);
        }
      }
      if ('form' in patch) {
        const incoming = asRecord(patch.form);
        if (!incoming) delete screens.form;
        else {
          const tabs = asRecords(incoming.tabs)
            .map((tab) => {
              const key = asString(tab.key);
              const label = cleanI18n(tab.label);
              const fields = [...new Set(asStrings(tab.fields).filter((name) => names.includes(name)))];
              return KEY.test(key) && label ? { key, label, fields } : null;
            })
            .filter((tab): tab is { key: string; label: Record<string, string> | string; fields: string[] } => tab !== null);
          const keys = tabs.map((tab) => tab.key);
          if (new Set(keys).size !== keys.length) return state;
          if (tabs.some((tab) => tab.fields.length === 0)) return state;
          setOrDelete(screens, 'form', tabs.length > 0 ? { tabs } : undefined);
        }
      }
      if ('view360' in patch) setOrDelete(screens, 'view360', patch.view360 === true ? true : undefined);
      setOrDelete(entity, 'screens', Object.keys(screens).length > 0 ? screens : undefined);
      return content(state, spec);
    }

    case 'rule.add':
    case 'rule.update':
    case 'rule.remove': {
      const spec = cloneJson(state.spec);
      const entity = asRecord(entitiesOf(spec)[action.entity]);
      if (!entity) return state;
      const rules = asRecords(entity.rules);
      const names = asRecords(entity.fields).map((field) => asString(field.name));
      if (action.type === 'rule.remove') {
        if (action.index === undefined || !rules[action.index]) return state;
        rules.splice(action.index, 1);
        setOrDelete(entity, 'rules', rules.length > 0 ? rules : undefined);
        return content(state, spec);
      }
      const normalizeRule = (rule: Json | undefined, fallbackKey: string): Json | null => {
        const key = asString(rule?.key) || fallbackKey;
        if (!KEY.test(key)) return null;
        const kind = RULE_KINDS.includes(asString(rule?.kind)) ? asString(rule?.kind) : 'validation';
        const description = asString(rule?.description).trim();
        if (description.length < 10 || description.length > 1000) return null;
        const fields = [...new Set(asStrings(rule?.fields).filter((name) => names.includes(name)))];
        if (fields.length === 0) return null;
        const out: Json = { key, kind, description, fields };
        const message = cleanI18n(rule?.message);
        if (message) out.message = message;
        return out;
      };
      if (action.type === 'rule.add') {
        if (rules.length >= DESIGNER_LIMITS.rulesPerEntity) return state;
        const free = firstFree('regle', rules.map((rule) => asString(rule.key)));
        const incoming = (action.rule as unknown as Json | undefined) ?? {};
        const draft: Json = { key: free.code, kind: 'validation', description: 'Règle à décrire dans ce dossier.', fields: names.slice(0, 1), ...incoming };
        const rule = normalizeRule(draft, free.code);
        if (!rule || rules.some((entry) => entry.key === rule.key)) return state;
        entity.rules = [...rules, rule];
        return content(state, spec);
      }
      if (action.index === undefined || !rules[action.index]) return state;
      const rule = normalizeRule({ ...rules[action.index], ...((action.rule as unknown as Json | undefined) ?? {}) }, asString(rules[action.index].key));
      if (!rule || rules.some((entry, index) => index !== action.index && entry.key === rule.key)) return state;
      rules[action.index] = rule;
      entity.rules = rules;
      return content(state, spec);
    }

    case 'relation.add': {
      const codes = entityCodes(state.spec);
      const relations = relationsOf(state.spec);
      if (relations.length >= DESIGNER_LIMITS.relations) return state;
      if (!codes.includes(action.source)) return state;
      const external = typeof action.targetModule === 'string' && action.targetModule.length > 0;
      if (!external && !codes.includes(action.target)) return state;
      if (external && (!MODULE_CODE_PATTERN.test(action.targetModule as string) || !ENTITY_CODE.test(action.target))) return state;
      const spec = cloneJson(state.spec);
      const domain = asString(readModule(spec).domain) || domainForCode(asString(readModule(spec).code) || 'module');
      const base = `${domain}.${action.source}.${action.target}`;
      const taken = relations.map((relation) => asString(relation.code));
      let code = base;
      let suffix = 2;
      while (taken.includes(code)) {
        code = `${domain}.${action.source}.${action.target}${suffix}`;
        suffix += 1;
      }
      if (!RELATION_CODE.test(code)) return state;
      const sourceEntity = readEntity(spec, action.source);
      const targetEntity = external ? null : readEntity(spec, action.target);
      const relation: Json = {
        code,
        source: { entity: action.source },
        target: external ? { module: action.targetModule, entity: action.target } : { entity: action.target },
        cardinality: action.source === action.target && !external ? '1-N' : 'N-1',
        labelForward: { fr: external ? action.target : entityPluralLabel(action.target, targetEntity) || entityLabel(action.target, targetEntity) },
        labelReverse: { fr: entityPluralLabel(action.source, sourceEntity) },
        missingModule: 'text',
      };
      spec.relations = [...relations, relation];
      return content(state, spec, state.layout, { kind: 'relation', code });
    }

    case 'relation.update': {
      const spec = cloneJson(state.spec);
      const relations = relationsOf(spec);
      const index = relations.findIndex((relation) => relation.code === action.code);
      if (index < 0) return state;
      const relation = relations[index];
      const patch = action.patch as Json;
      const codes = entityCodes(spec);
      if ('code' in patch) {
        const next = asString(patch.code);
        if (!RELATION_CODE.test(next) || relations.some((entry, i) => i !== index && entry.code === next)) return state;
        relation.code = next;
      }
      if ('source' in patch) {
        const source = asRecord(patch.source);
        const entity = asString(source?.entity) || asString(asRecord(relation.source)?.entity);
        if (!codes.includes(entity)) return state;
        const out: Json = { entity };
        const field = asString(source?.field);
        if (field) {
          if (!asRecords(asRecord(entitiesOf(spec)[entity])?.fields).some((entry) => entry.name === field)) return state;
          out.field = field;
        }
        relation.source = out;
      }
      if ('target' in patch) {
        const target = asRecord(patch.target);
        const module = asString(target?.module);
        const entity = asString(target?.entity) || asString(asRecord(relation.target)?.entity);
        if (!ENTITY_CODE.test(entity)) return state;
        if (module && !MODULE_CODE_PATTERN.test(module)) return state;
        if (!module && !codes.includes(entity)) return state;
        const out: Json = module ? { module, entity } : { entity };
        const displayField = asString(target?.displayField);
        if (displayField) {
          if (!FIELD_NAME.test(displayField)) return state;
          if (!module && !asRecords(asRecord(entitiesOf(spec)[entity])?.fields).some((entry) => entry.name === displayField)) return state;
          out.displayField = displayField;
        }
        relation.target = out;
      }
      if ('cardinality' in patch) {
        if (!CARDINALITIES.includes(asString(patch.cardinality))) return state;
        relation.cardinality = patch.cardinality;
      }
      (['labelForward', 'labelReverse'] as const).forEach((slot) => {
        if (slot in patch) {
          const label = cleanI18n(patch[slot]);
          if (label) relation[slot] = label;
        }
      });
      if ('required' in patch) setOrDelete(relation, 'required', patch.required === true ? true : undefined);
      if ('missingModule' in patch) setOrDelete(relation, 'missingModule', MISSING_MODULE.includes(asString(patch.missingModule)) ? patch.missingModule : undefined);
      if ('archimate' in patch) {
        setOrDelete(relation, 'archimate', (DESIGNER_ARCHIMATE_TYPES as readonly string[]).includes(asString(patch.archimate)) ? patch.archimate : undefined);
      }
      spec.relations = relations;
      const selection: DesignerSelection =
        state.selection?.kind === 'relation' && state.selection.code === action.code ? { kind: 'relation', code: asString(relation.code) } : state.selection;
      return content(state, spec, state.layout, selection);
    }

    case 'relation.remove': {
      const relations = relationsOf(state.spec);
      if (!relations.some((relation) => relation.code === action.code)) return state;
      const spec = cloneJson(state.spec);
      const remaining = relationsOf(spec).filter((relation) => relation.code !== action.code);
      setOrDelete(spec, 'relations', remaining.length > 0 ? remaining : undefined);
      const selection = state.selection?.kind === 'relation' && state.selection.code === action.code ? null : state.selection;
      return content(state, spec, state.layout, selection);
    }

    case 'page.add':
    case 'page.update':
    case 'page.remove': {
      const spec = cloneJson(state.spec);
      const pages = pagesOf(spec);
      const codes = entityCodes(spec);
      if (action.type === 'page.remove') {
        const index = pages.findIndex((page) => page.key === action.key);
        if (index < 0) return state;
        pages.splice(index, 1);
        setOrDelete(spec, 'pages', pages.length > 0 ? pages : undefined);
        cascadePageKeyInActions(pages, asString(action.key), null);
        dropTarget(spec, { page: asString(action.key) });
        const navigation = asRecord(spec.navigation);
        if (navigation && navigation.homePage === action.key) delete navigation.homePage;
        const selection = state.selection?.kind === 'page' && state.selection.key === action.key ? null : state.selection;
        return content(state, spec, movePageReviews(state.layout,asString(action.key)), selection);
      }
      const applyPatch = (page: Json, patch: Json, isNew: boolean): Json | null => {
        const next: Json = { ...page, ...(page.kind === 'other' && patch.kind === 'custom' ? otherPagePatch(page as unknown as ModuleSpecPage) : {}) };
        if ('key' in patch) {
          const key = asString(patch.key);
          if (!KEY.test(key)) return null;
          next.key = key;
        }
        if ('kind' in patch) {
          if (!PAGE_KINDS.includes(asString(patch.kind)) || (patch.kind === 'other' && (isNew || page.kind !== 'other'))) return null;
          next.kind = patch.kind;
        }
        if ('label' in patch) {
          const label = cleanI18n(patch.label);
          if (!label) return null;
          next.label = label;
        }
        if ('slug' in patch) {
          const slug = asString(patch.slug);
          if (slug && !SLUG_PATTERN.test(slug)) return null;
          setOrDelete(next, 'slug', slug || undefined);
        }
        if ('portals' in patch) {
          const portals = [...new Set(asStrings(patch.portals).filter((portal) => PORTALS.includes(portal)))];
          if (portals.length === 0) return null;
          next.portals = portals;
        }
        if ('entities' in patch) {
          const entities = [...new Set(asStrings(patch.entities).filter((code) => codes.includes(code)))];
          setOrDelete(next, 'entities', entities.length > 0 ? entities : undefined);
        }
        if ('description' in patch) {
          const description = asString(patch.description).trim();
          if (description.length < DESCRIPTION_MIN || description.length > DESCRIPTION_MAX) return null;
          next.description = description;
        }
        if ('breadcrumb' in patch) {if(typeof patch.breadcrumb!=='boolean')return null;next.breadcrumb=patch.breadcrumb;}
        // QUIZ-NATIVE2 GAP-Q14 : « phase de » une autre page (jamais elle-même, jamais une phase, jamais une page qui a ses phases).
        if ('phaseOf' in patch) {
          const host = asString(patch.phaseOf);
          if (host && (host === next.key || !pages.some((other) => other.key === host && !other.phaseOf) || pages.some((other) => other.phaseOf === page.key))) return null;
          setOrDelete(next, 'phaseOf', host || undefined);
        }
        if ('sourceTemplate' in patch) setOrDelete(next, 'sourceTemplate', asString(patch.sourceTemplate).slice(0, 120) || undefined);
        if ('mockupRef' in patch) setOrDelete(next, 'mockupRef', asString(patch.mockupRef).slice(0, 300) || undefined);
        // UPT-2079 L14b : morceaux de la page `custom` (schéma v1.2) — structure invalide refusée en bloc, objet inconnu retiré.
        if ('prototypeRef' in patch) {
          const result = sanitizePrototypeRef(patch.prototypeRef);
          if (!result.ok) return null;
          setOrDelete(next, 'prototypeRef', result.value);
        }
        for (const key of ['reads', 'writes'] as const) {
          if (!(key in patch)) continue;
          const result = sanitizeEntityList(patch[key], codes);
          if (!result.ok) return null;
          setOrDelete(next, key, result.value);
        }
        if ('layout' in patch) {
          const result = sanitizeLayout(patch.layout);
          if (!result.ok) return null;
          setOrDelete(next, 'layout', result.value);
        }
        if ('screen' in patch) {
          const screen=asRecord(patch.screen);
          if(screen && Array.isArray(screen.blocks) && screen.blocks.length<=60 && next.kind==='custom') {
            next.screen=cloneJson({columns:12,blocks:screen.blocks});delete next.slots;
            spec.specVersion='1.3';
          } else if(patch.screen==null) delete next.screen; else return null;
        }
        if ('slots' in patch) {
          const result = sanitizeSlots(patch.slots, codes);
          if (!result.ok) return null;
          setOrDelete(next, 'slots', result.value);
        }
        if ('actions' in patch) {
          const result = sanitizeActions(patch.actions, codes);
          if (!result.ok) return null;
          setOrDelete(next, 'actions', result.value);
        }
        if ('slots' in patch || 'actions' in patch) reconcileActionSlots(next);
        if (isNew && !next.key) return null;
        return next;
      };
      if (action.type === 'page.add') {
        if (pages.length >= DESIGNER_LIMITS.pages) return state;
        const free = firstFree('page', pages.map((page) => asString(page.key)));
        const page = applyPatch(newPage(free.index) as unknown as Json, (action.patch as unknown as Json) ?? {}, true);
        if (!page || pages.some((entry) => entry.key === page.key)) return state;
        spec.pages = [...pages, page];
        if (usesCustomPageProperties(page) && (spec.specVersion === '1.0' || spec.specVersion === '1.1')) spec.specVersion = '1.2';
        return content(state, spec, state.layout, { kind: 'page', key: asString(page.key) });
      }
      const index = pages.findIndex((page) => page.key === action.key);
      if (index < 0) return state;
      const migratedOther = pages[index].kind === 'other' && action.patch?.kind === 'custom';
      const page = applyPatch(pages[index], (action.patch as unknown as Json) ?? {}, false);
      if (!page || pages.some((entry, i) => i !== index && entry.key === page.key)) return state;
      if (page.key !== action.key) {
        cascadePageKeyInActions(pages, asString(action.key), asString(page.key));
        asRecords(asRecord(spec.navigation)?.menus).forEach((menu) => asRecords(menu.items).forEach((item) => {
          if (item.page === action.key) item.page = page.key;
        }));
        const navigation = asRecord(spec.navigation);
        if (navigation && navigation.homePage === action.key) navigation.homePage = page.key;
      }
      pages[index] = page;
      spec.pages = pages;
      // Une page de phase quitte le menu et l'accueil (sa navigation mène à la page hôte).
      if (page.phaseOf) {
        dropTarget(spec, { page: asString(page.key) });
        const navigation = asRecord(spec.navigation);
        if (navigation && navigation.homePage === page.key) navigation.homePage = page.phaseOf;
      }
      if (migratedOther) spec.specVersion = '1.3';
      // UPT-2079 L14b : une page `custom` (ou l'une de ses propriétés) est du schéma v1.2 ; le serveur le ferait de toute façon (`spec.version_bumped`).
      if (usesCustomPageProperties(page) && (spec.specVersion === '1.0' || spec.specVersion === '1.1')) spec.specVersion = '1.2';
      const selection: DesignerSelection =
        state.selection?.kind === 'page' && state.selection.key === action.key ? { kind: 'page', key: asString(page.key) } : state.selection;
      return content(state, spec, page.key===action.key?state.layout:movePageReviews(state.layout,asString(action.key),asString(page.key)), selection);
    }

    case 'navigation.toggle': {
      const entity = asString(action.item.entity);
      const page = asString(action.item.page);
      if ((entity && !entityCodes(state.spec).includes(entity)) || (page && !pagesOf(state.spec).some((entry) => entry.key === page))) return state;
      if (!entity && !page) return state;
      const spec = cloneJson(state.spec);
      const navigation: Json = asRecord(spec.navigation) ?? {};
      const menus = asRecords(navigation.menus);
      const menu: Json = menus[0] ?? { key: 'main', label: { fr: asString(readModule(spec).name) || 'Menu' }, items: [] };
      const items = asRecords(menu.items);
      const match = (item: Json): boolean => (entity ? item.entity === entity : item.page === page);
      const next = items.some(match) ? items.filter((item) => !match(item)) : [...items, entity ? { entity } : { page }];
      menu.items = next;
      if (menus.length === 0) menus.push(menu);
      else menus[0] = menu;
      // `items` exige au moins une entrée : un menu vidé est retiré avec sa navigation.
      if (next.length === 0) {
        menus.shift();
        if (menus.length === 0) delete navigation.menus;
        else navigation.menus = menus;
      } else navigation.menus = menus;
      if (Object.keys(navigation).length === 0) delete spec.navigation;
      else spec.navigation = navigation;
      return content(state, spec);
    }

    case 'navigation.home': {
      const valid = entityCodes(state.spec).includes(action.key) || pagesOf(state.spec).some((page) => page.key === action.key);
      if (!valid || asRecord(state.spec.navigation)?.homePage === action.key) return state;
      const spec = cloneJson(state.spec);
      spec.navigation = { ...(asRecord(spec.navigation) ?? {}), homePage: action.key };
      return content(state, spec);
    }

    case 'navigation.move': {
      const menu = menuOf(state.spec);
      const items = asRecords(menu?.items);
      if (!menu || action.from === action.to || !items[action.from] || action.to < 0 || action.to >= items.length) return state;
      const spec = cloneJson(state.spec);
      const target = menuOf(spec) as Json;
      const list = asRecords(target.items);
      const [moved] = list.splice(action.from, 1);
      list.splice(action.to, 0, moved);
      target.items = list;
      return content(state, spec);
    }

    case 'navigation.group.add':
    case 'navigation.group.update':
    case 'navigation.group.remove':
    case 'navigation.group.move':
    case 'navigation.item.add':
    case 'navigation.item.update':
    case 'navigation.item.remove':
    case 'navigation.item.move':
    case 'navigation.replace': {
      const spec = cloneJson(state.spec);
      let changed = false;
      switch (action.type) {
        case 'navigation.group.add': changed = groupAdd(spec, action.label); break;
        case 'navigation.group.update': changed = groupUpdate(spec, action.index, action.patch); break;
        case 'navigation.group.remove': changed = groupRemove(spec, action.index); break;
        case 'navigation.group.move': changed = groupMove(spec, action.from, action.to); break;
        case 'navigation.item.add': changed = itemAdd(spec, action.group, action.target, action.at); break;
        case 'navigation.item.update': changed = itemUpdate(spec, action.group, action.index, action.patch); break;
        case 'navigation.item.remove': changed = itemRemove(spec, action.group, action.index); break;
        case 'navigation.item.move': changed = itemMove(spec, action.group, action.from, action.to, action.toGroup); break;
        default: changed = navigationReplace(spec, action.navigation, action.addedPages);
      }
      if (!changed) return state;
      normalizeNavigation(spec);
      bumpNavigationVersion(spec);
      return content(state, spec);
    }

    case 'save.pending':
      return state.save.status === 'pending' ? state : { ...state, save: { ...state.save, status: 'pending' } };
    case 'save.started':
      return { ...state, save: { ...state.save, status: 'saving', error: null } };
    case 'save.done': {
      const sameSpec = canonicalJson(state.spec) === canonicalJson(action.savedSpec??action.draft.spec);
      const sameLayout = canonicalJson(state.layout) === canonicalJson(action.draft.layout ?? EMPTY_LAYOUT);
      const dirty = !(sameSpec && sameLayout);
      return {
        ...state,
          spec:sameSpec?action.draft.spec:state.spec,
        dirty,
        checksum: action.draft.checksum,
        validation: action.draft.validation ?? state.validation,
        save: { status: dirty ? 'pending' : 'saved', at: action.draft.updatedAt ?? new Date().toISOString(), error: null },
      };
    }
    case 'save.conflict':
      return { ...state, save: { ...state.save, status: 'conflict', error: null } };
    case 'save.failed':
      return { ...state, save: { ...state.save, status: 'error', error: action.error } };
    default:
      return state;
  }
}

/** Compteurs de la barre d'outils (`{entités} · {champs} · {relations}`). */
export const designerCounts = (spec: DesignModuleSpecJson): { entities: number; fields: number; relations: number; pages: number } => {
  const entities = entitiesOf(spec);
  return {
    entities: Object.keys(entities).length,
    fields: Object.values(entities).reduce<number>((sum, entity) => sum + asRecords(asRecord(entity)?.fields).length, 0),
    relations: relationsOf(spec).length,
    pages: pagesOf(spec).length,
  };
};

/** Nombre d'erreurs du rapport (serveur sinon local) sous `/entities/<code>`. */
export const entityErrorCount = (state: Pick<DesignerState, 'validation' | 'localIssues'>, code: string): number => {
  const errors = state.validation ? state.validation.errors : state.localIssues;
  const prefix = `/entities/${code}`;
  return errors.filter((issue) => issue.path === prefix || issue.path.startsWith(`${prefix}/`)).length;
};
